import { Controller } from "@hotwired/stimulus"

// Asides, in the composer. Select a phrase in the field and an "Aside" control
// appears; press it and a row opens beneath the draft with the phrase and one
// line to write under it. Each row is a set of nested fields the post saves
// with; a phrase edited out of the draft is said so beneath its row, and the
// server drops it rather than misplace it.
export default class extends Controller {
  static targets = [ "field", "control", "list", "template", "row" ]
  static values = { max: { type: Number, default: 3 }, min: { type: Number, default: 2 }, longest: { type: Number, default: 200 } }

  connect() {
    this.onSelection = () => this.check()
    document.addEventListener("selectionchange", this.onSelection)
    this.check()
  }

  disconnect() {
    document.removeEventListener("selectionchange", this.onSelection)
  }

  // The control shows only with a phrase selected in the field, and room for another.
  check() {
    this.anchor()
    if (!this.hasControlTarget) return

    const phrase = this.selection()
    this.controlTarget.hidden = !(phrase && this.rows.length < this.maxValue)
  }

  add() {
    const phrase = this.selection()
    if (!phrase || this.rows.length >= this.maxValue) return

    const field = this.fieldTarget
    const before = field.value.slice(0, field.selectionEnd)
    const occurrence = before.split(phrase).length - 1

    const row = this.templateTarget.content.firstElementChild.cloneNode(true)
    const index = Date.now()
    row.querySelectorAll("[name]").forEach((input) => { input.name = input.name.replace("INDEX", index) })
    row.querySelector("[data-asides-role='phrase']").textContent = phrase
    row.querySelector("[data-asides-role='phrase-field']").value = phrase
    row.querySelector("[data-asides-role='occurrence-field']").value = occurrence
    this.listTarget.append(row)
    this.listTarget.hidden = false

    row.querySelector("[data-asides-role='text']").focus()
    this.check()
  }

  // A saved aside is marked to go and hidden, so the post is told; a new one
  // simply goes.
  remove(event) {
    const row = event.currentTarget.closest("[data-asides-target='row']")
    const destroy = row.querySelector("[data-asides-role='destroy']")
    if (destroy) {
      destroy.value = "1"
      row.hidden = true
    } else {
      row.remove()
    }
    this.listTarget.hidden = this.rows.length === 0
    this.check()
    this.fieldTarget.focus()
  }

  get rows() {
    return this.rowTargets.filter((row) => !row.hidden)
  }

  // Return in an aside's line goes back to the draft, not to the button; an
  // empty line and Escape take the row away.
  keydown(event) {
    if (event.key === "Enter") {
      event.preventDefault()
      this.fieldTarget.focus()
    } else if (event.key === "Escape" && event.currentTarget.value === "") {
      this.remove(event)
    }
  }

  // The selected phrase, if it is one: within a paragraph, and neither a word
  // nor a passage. Whitespace around it is not the phrase.
  selection() {
    if (!this.hasFieldTarget) return null

    const field = this.fieldTarget
    const phrase = field.value.slice(field.selectionStart, field.selectionEnd).trim()
    if (phrase.length < this.minValue || phrase.length > this.longestValue || phrase.includes("\n")) return null

    return phrase
  }

  // Each row says whether its phrase is still in the draft.
  anchor() {
    const body = this.hasFieldTarget ? this.fieldTarget.value : ""
    this.rows.forEach((row) => {
      const phrase = row.querySelector("[data-asides-role='phrase-field']").value
      const occurrence = Number(row.querySelector("[data-asides-role='occurrence-field']").value)
      row.classList.toggle("aside-row-lost", body.split(phrase).length - 1 < occurrence)
    })
  }
}
